import React, { type FC, useContext } from 'react';

import { getI18nData } from '@lucid/utils';

import type { Lucid } from '../../../types/types';
import Box from '../../box/box';
import Icon from '../../icon/icon';
import Text from '../../text/text';
import Image from '../../image/image';
import Link from '../../link/link';
import { CardContext } from '../card';
import LayoutContext from '../../context/layout-context';

export type CardTagsProps = {
  /** The list of tags to be rendered */
  items?: Lucid.Directus.Generated.Links[] | Lucid.Product.Name[];
  /** The label to be displayed above the tags
   * @default 'Tags:'
   */
  label?: string;
};

const CardTags: FC<CardTagsProps> = ({ items, label, ...rest }) => {
  const { shortCode } = useContext(LayoutContext);
  const { i18nData, isFullCardLink } = useContext(CardContext);
  const i18n = getI18nData(i18nData, shortCode);

  return items?.length ? (
    <Box
      {...rest}
      css={{
        label: 'cardTags',
      }}
      data-card-element="tags"
    >
      <Box css={{ display: 'flex' }}>
        <Icon color="coolGrey.70" css={{ height: '100%' }} type="Tag" />
        <Text
          css={theme => ({
            margin: `0 0 ${theme.space.s16}px ${theme.space.s8}px`,
            fontWeight: theme.fontWeights.fw500,
            color: theme.colors.coolGrey[70],
          })}
        >
          {label || i18n('mktg-learning-campus-collection-tags') || 'Tags:'}
        </Text>
      </Box>
      <Box
        as="ul"
        css={theme => ({
          display: 'flex',
          gap: theme.space.s8,
          justifyContent: 'flex-start',
          flexWrap: 'wrap',
          listStyle: 'none',
          margin: '0',
        })}
      >
        {items.map((tag, idx) => {
          const isProductTag = typeof tag === 'string';

          return (
            <li
              key={`card-tag-${idx}`}
              css={theme => [
                {
                  display: 'flex',
                  alignItems: 'center',
                  margin: 0,
                },
                isProductTag && {
                  background: theme.colors.coolGrey[5],
                  padding: `${theme.space.s8}px 0 ${theme.space.s8}px ${theme.space.s8}px`,
                },
              ]}
            >
              {isProductTag ? (
                <Image
                  css={{
                    height: '15px',
                  }}
                  imageAlt={`${tag} tag`}
                  imageSrc={`https://cdn-cashy-static-assets.lucid.co/lucid/logos/${tag?.toLowerCase()}.svg`}
                />
              ) : (
                <Link
                  css={theme => [isFullCardLink && { zIndex: theme.zIndices.z500 }]}
                  href={tag?.final_url}
                  variant="cardTag"
                >
                  {tag?.default_link_text}
                </Link>
              )}
            </li>
          );
        })}
      </Box>
    </Box>
  ) : null;
};
export { CardTags };
export default CardTags;
